import { useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useResource, formatDateTime } from "../../lib/hooks";
import { useAuth } from "../../context/AuthContext";
import { EntityForm } from "../../components/EntityForm";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import type { Participant } from "../../lib/types";
import { type QuarantineRow, streamOf } from "../../lib/intakeStreams";
import { buildReferralFields, prefillFromPayload, nestedFromPayload } from "../../lib/referralIntake";
import { senderOf, detailsOf, previousRefNote, containsLink } from "../../lib/intakeDisplay";

/**
 * The Enquiry queue — general "get in touch" website forms (contact us,
 * request a callback). These are not referrals, so they stay out of the
 * referral queue, but a genuine one can turn out to be about a real
 * participant: "Promote to referral" opens the ordinary referral form
 * prefilled from the enquiry, so it becomes a proper referral in one step
 * (keeping its own reference). Anything that is just a question is closed with
 * a reason. Rows share the quarantine table and its promote/reject actions
 * with the Referrals page; only the presentation and routing differ.
 */

type Dialog =
    | { kind: "promote"; item: QuarantineRow }
    | { kind: "dismiss"; item: QuarantineRow }
    | null;

/**
 * A promoted enquiry becomes an ordinary referral and is filed with its own
 * TC-REF number — the referral register stays consistent, and reporting that
 * expects TC-REF keeps working. The enquiry's own reference is carried into
 * the notes instead, so the trail from website enquiry to referral survives.
 */
const withEnquiryOrigin = (
    prefill: Record<string, unknown>,
    refCode: string | null,
): Record<string, unknown> => {
    if (!refCode) return prefill;
    const notes = typeof prefill.additional_notes === "string" ? prefill.additional_notes : "";
    return {
        ...prefill,
        additional_notes: [`Promoted from website enquiry ${refCode}.`, notes].filter(Boolean).join("\n"),
    };
};

export const EnquiryQueue = () => {
    const { user } = useAuth();
    const canDecide = user?.role === "admin" || user?.role === "manager";

    const quarantine = useResource<QuarantineRow[]>(canDecide ? "/intake/quarantine" : null);
    const participants = useResource<Participant[]>(canDecide ? "/participants" : null);
    const companies = useResource<Array<{ id: string; name: string }>>(canDecide ? "/companies" : null);
    const contacts = useResource<Array<{ id: string; first_name: string; last_name: string; company_id: string | null }>>(canDecide ? "/contacts" : null);

    const [dialog, setDialog] = useState<Dialog>(null);
    const [open, setOpen] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const enquiries = useMemo(
        () => (quarantine.data ?? []).filter((q) => streamOf(q.form_name) === "enquiry"),
        [quarantine.data]
    );

    const fieldDeps = {
        companies: companies.data ?? [],
        contacts: contacts.data ?? [],
        participants: participants.data ?? [],
    };

    // Promote = create the referral through the ordinary validated form, then
    // stamp the enquiry with what it became so the queue empties honestly. The
    // referral carries the enquiry's own reference number.
    const promote = async (item: QuarantineRow, saved?: unknown) => {
        const referralId = (saved as { id?: string } | undefined)?.id;
        setDialog(null);
        if (!referralId) { quarantine.reload(); return; }
        try {
            await api.post(`/intake/quarantine/${item.id}/promote`, { referralId });
        } catch (err) {
            setError(err instanceof Error ? err.message : "Created the referral but could not clear the enquiry");
        }
        quarantine.reload();
    };

    if (!canDecide) return <div className="error-banner">This page is for admins and managers.</div>;
    if (quarantine.error) return <div className="error-banner">{quarantine.error}</div>;

    return (
        <div style={{ display: "grid", gap: "1rem" }}>
            {dialog?.kind === "promote" && (
                <EntityForm
                    title={`Promote enquiry to referral${dialog.item.ref_code ? ` — ${dialog.item.ref_code}` : ""}`}
                    path="/referrals"
                    fields={buildReferralFields({ ...fieldDeps, nested: nestedFromPayload(dialog.item.payload) })}
                    initial={withEnquiryOrigin(prefillFromPayload(dialog.item.payload), dialog.item.ref_code)}
                    subtitle="Prefilled from the enquiry — check it, link or create the participant, then save. The referral is filed with its own TC-REF number; this enquiry's reference is kept in the notes."
                    onClose={() => setDialog(null)}
                    onSaved={(saved) => void promote(dialog.item, saved)} />
            )}
            {dialog?.kind === "dismiss" && (
                <ConfirmDialog
                    title="Close this enquiry?"
                    message="It stays in the quarantine record with your reason — nothing is deleted."
                    confirmLabel="Close enquiry" requireReason reasonLabel="Reason (answered, spam, duplicate…)"
                    request={{ method: "post", path: `/intake/quarantine/${dialog.item.id}/reject` }}
                    onClose={() => setDialog(null)}
                    onDone={() => { setDialog(null); quarantine.reload(); }} />
            )}

            {error && <div className="error-banner">{error}</div>}

            <div className="card">
                <div className="card-head">
                    <div>
                        <h2>Enquiries</h2>
                        <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginTop: "0.15rem" }}>
                            Contact and callback requests from the website. Promote a genuine participant
                            enquiry into a referral, or close it with a reason once it's answered.
                        </p>
                    </div>
                    <span className="chip info">{enquiries.length} pending</span>
                </div>
                <div className="card-body">
                    {enquiries.length === 0 ? (
                        <div className="empty">
                            Nothing waiting. Contact and callback forms from the website arrive here;
                            referrals go to the Referrals page and other forms to the Website inbox.
                        </div>
                    ) : (
                        <div style={{ display: "grid", gap: "0.5rem" }}>
                            {enquiries.map((item) => (
                                <div key={item.id} className="file-row" style={{ alignItems: "flex-start" }}>
                                    <span className="mono" style={{ whiteSpace: "nowrap" }}>{formatDateTime(item.received_at)}</span>
                                    <span style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 600 }}>
                                            {item.ref_code && (
                                                <span className="chip mute mono" style={{ marginRight: 6, fontWeight: 700 }}
                                                    title={previousRefNote(item.previous_ref_code)}>{item.ref_code}</span>
                                            )}
                                            {senderOf(item.payload)}
                                            {item.form_name && <span className="chip info" style={{ marginLeft: 6 }}>{item.form_name}</span>}
                                            {containsLink(item.payload) && (
                                                <span className="chip warn" style={{ marginLeft: 6 }}
                                                    title="This submission contains a web address. It is shown as plain text and is not clickable — do not open it unless you trust the sender.">
                                                    Contains a link
                                                </span>
                                            )}
                                        </div>
                                        {open === item.id && (
                                            <dl className="kv" style={{ marginTop: "0.5rem" }}>
                                                {detailsOf(item.payload).map(([k, v]) => (
                                                    <span key={k} style={{ display: "contents" }}>
                                                        <dt style={{ textTransform: "capitalize" }}>{k}</dt>
                                                        <dd style={{ whiteSpace: "pre-wrap" }}>{v}</dd>
                                                    </span>
                                                ))}
                                            </dl>
                                        )}
                                    </span>
                                    <span style={{ display: "flex", gap: "0.35rem", whiteSpace: "nowrap" }}>
                                        <button className="icon-btn" onClick={() => setOpen(open === item.id ? null : item.id)}>
                                            {open === item.id ? "Hide details" : "Details"}
                                        </button>
                                        <button className="icon-btn" onClick={() => setDialog({ kind: "promote", item })}>
                                            Promote to referral
                                        </button>
                                        <button className="icon-btn danger" onClick={() => setDialog({ kind: "dismiss", item })}>Close</button>
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
